import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useSearchParams } from 'react-router-dom'
import { updateEventTicket, uploadEventTicketAvatar, validateTicket, joinCommunityGuest } from 'api'
import { getAvatarUrl } from '../../../../api/utils'
import noAvatar from '../../../../assets/images/no-avatar-upload.png'
import { 
  User, 
  Mail, 
  AlertTriangle, 
  Info, 
  X, 
  ChevronLeft, 
  ChevronRight,
  Upload,
  Link,
  MessageSquare,
  UserPlus
} from 'lucide-react'
import {
  Container,
  StepHeader,
  StepTitle,
  StepSubtitle,
  StepIndicator,
  StepDot,
  AvatarSection,
  AvatarContainer,
  AvatarWrapper,
  AvatarImage,
  AvatarLabel,
  FormSection,
  FormField,
  FieldLabel,
  InputWrapper,
  StyledInput,
  StyledTextarea,
  AlertContainer,
  Alert,
  AlertHeader,
  AlertTitle,
  AlertContent,
  AlertButton,
  CloseButton,
  SliderSection,
  SliderBlock,
  SliderTitle,
  SliderWrapper,
  CustomSlider,
  SliderTrack,
  SliderThumb,
  SliderLabels,
  SliderLabel,
  NavigationSection,
  PrimaryButton,
  SecondaryButton,
  LoadingOverlay,
  LoadingSpinner,
  ForgotButton,
  StyledFab
} from './styled'

const EventTicketUpdate = ({ visibleModal, myTicket, event, reload }) => {
  const [searchParams] = useSearchParams()
  const [validation, setValidation] = useState(!myTicket?.user?.is_active || false)
  const [member, setMember] = useState(!event?.membership || event?.membership?.role === 'GU')
  const [selectedFile, setSelectedFile] = useState(null)
  const [activeStep, setActiveStep] = useState(1)
  const [validationValue] = useState(searchParams.get('key') || null)
  const [avatar, setAvatar] = useState(
    getAvatarUrl(myTicket?.user, noAvatar)
  )
  const [isLoading, setIsLoading] = useState(false)
  const [sliderValues, setSliderValues] = useState({})

  const steps = ['About you', 'Additional info']
  const stepTitles = {
    1: 'Tell us about yourself',
    2: 'Additional information'
  }
  const stepSubtitles = {
    1: 'Help others get to know you better',
    2: 'Share your links and describe yourself'
  }

  const valueTextMap = {
    0: 'Learn',
    1: 'None',
    2: 'Share',
  }

  const [ticketData, setTicketData] = useState({
    link_1: myTicket?.link_1 || '',
    link_2: myTicket?.link_2 || '',
    link_3: myTicket?.link_3 || '',
    info: myTicket?.user?.alias || '',
    name: myTicket?.user ? `${myTicket.user.first_name} ${myTicket.user.last_name}` : '',
    description: myTicket?.user?.description || '',
    social: myTicket?.social || '',
    needs: myTicket?.needs ? myTicket.needs : [],
    offers: myTicket?.offers ? myTicket.offers : [],
  })

  const checkValidation = async () => {
    if (validationValue !== null) {
      try {
        const data = { key: validationValue }
        await validateTicket(myTicket?.slug, data)
        setValidation(false)
      } catch (error) {
        console.error('Validation error -> ', error)
      }
    }
  }

  const handleNext = async () => {
    setIsLoading(true)
    try {
      await updateEventTicket(myTicket?.slug, ticketData)
      reload()
      
      if (activeStep === steps.length) {
        visibleModal(false)
        setActiveStep(1)
      } else {
        setActiveStep((prevStep) => prevStep + 1)
      }
    } catch (error) {
      console.error('Update event error -> ', error)
    } finally {
      setIsLoading(false)
    }
  }

  const joinTheCommunity = async () => {
    setIsLoading(true)
    try {
      const data = { ticket: myTicket.slug }
      await joinCommunityGuest(data)
      reload()
      setMember(false)
    } catch (error) {
      console.error('Join community error -> ', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1)
  }

  const handleUpload = async () => {
    if (selectedFile) {
      try {
        await uploadEventTicketAvatar(myTicket.slug, selectedFile)
      } catch (error) {
        console.error('Upload error:', error)
      }
    }
  }

  const handleSliderChange = (tag, value) => {
    setSliderValues(prev => ({ ...prev, [tag]: value }))
    
    let needs = ticketData?.needs?.filter((item) => item !== tag) || []
    let offers = ticketData?.offers?.filter((item) => item !== tag) || []
    
    if (value === 0) {
      needs = [...needs, tag]
    } else if (value === 2) {
      offers = [...offers, tag]
    }
    
    setTicketData((prevData) => ({
      ...prevData,
      needs: needs,
      offers: offers,
    }))
  }

  const handleInputChange = (event) => {
    const { name, value } = event.target
    setTicketData((prevData) => ({
      ...prevData,
      [name]: value,
    }))
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      
      reader.onload = (event) => {
        setSelectedFile(file)
        setAvatar(event.target.result)
      }
      
      reader.readAsDataURL(file)
    }
  }

  const openFileInput = () => {
    document.getElementById('avatarInput').click()
  }

  const getSliderValue = (tagName) => {
    if (sliderValues[tagName] !== undefined) {
      return sliderValues[tagName]
    }
    
    if (ticketData.needs.includes(tagName)) {
      return 0
    } else if (ticketData.offers.includes(tagName)) {
      return 2
    }
    return 1
  }

  const CustomSliderComponent = ({ tag, value, onChange }) => {
    const handleClick = (event) => {
      const rect = event.currentTarget.getBoundingClientRect()
      const x = event.clientX - rect.left
      const percentage = x / rect.width
      const newValue = Math.round(percentage * 2)
      onChange(tag.name, Math.max(0, Math.min(2, newValue)))
    }

    return (
      <SliderBlock>
        <SliderTitle>{tag.name}</SliderTitle>
        <SliderWrapper>
          <CustomSlider onClick={handleClick}>
            <SliderTrack value={value} />
            <SliderThumb value={value} />
          </CustomSlider>
          <SliderLabels>
            <SliderLabel active={value === 0}>Learn</SliderLabel>
            <SliderLabel active={value === 1}>None</SliderLabel>
            <SliderLabel active={value === 2}>Share</SliderLabel>
          </SliderLabels>
        </SliderWrapper>
      </SliderBlock>
    )
  }

  useEffect(() => {
    if (selectedFile !== null) {
      handleUpload()
    }
  }, [selectedFile])

  useEffect(() => {
    if (validationValue !== null) {
      checkValidation()
    }
  }, [])

  // Initialize slider values
  useEffect(() => {
    if (event?.tags) {
      const initialValues = {}
      event.tags.forEach(tag => {
        initialValues[tag.name] = getSliderValue(tag.name)
      })
      setSliderValues(initialValues)
    }
  }, [event?.tags, ticketData.needs, ticketData.offers])

  return (
    <Container>
      {isLoading && (
        <LoadingOverlay>
          <LoadingSpinner />
        </LoadingOverlay>
      )}
      
      <StepHeader>
        <StepTitle>{stepTitles[activeStep]}</StepTitle>
        <StepSubtitle>{stepSubtitles[activeStep]}</StepSubtitle>
        <StepIndicator>
          {steps.map((_, index) => (
            <StepDot key={index} active={index + 1 <= activeStep} />
          ))}
        </StepIndicator>
      </StepHeader>

      {/* Step 1: About You */}
      {activeStep === 1 && (
        <div>
          {/* Community Join Alert */}
          {member && (
            <AlertContainer>
              <Alert variant="info" isOpen={member}>
                <AlertHeader>
                  <AlertTitle variant="info">
                    <UserPlus size={18} />
                    Join {event.group?.name}?
                  </AlertTitle>
                  <CloseButton onClick={() => setMember(false)}>
                    <X size={16} />
                  </CloseButton>
                </AlertHeader>
                <AlertContent>
                  Join our community and get access to all our events and resources. 
                  Get in touch with all members and take advantage of promotions and discounts.
                </AlertContent>
                <AlertButton variant="info" onClick={joinTheCommunity}>
                  Join Community
                </AlertButton>
              </Alert>
            </AlertContainer>
          )}

          {/* Email Validation Alert */}
          {validation && (
            <AlertContainer>
              <Alert variant="warning" isOpen={validation}>
                <AlertHeader>
                  <AlertTitle variant="warning">
                    <Mail size={18} />
                    Email validation pending
                  </AlertTitle>
                  <CloseButton onClick={() => setValidation(false)}>
                    <X size={16} />
                  </CloseButton>
                </AlertHeader>
                <AlertContent>
                  You should have an email from our partner <strong>HiveSpace</strong>. 
                  Please check your inbox and follow the instructions. You may have received it in your spam folder.
                </AlertContent>
              </Alert>
            </AlertContainer>
          )}

          <AvatarSection>
            <AvatarContainer>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="avatarInput"
                type="file"
                onChange={handleFileChange}
              />
              <AvatarWrapper onClick={openFileInput}>
                <AvatarImage src={avatar} alt="Avatar" />
              </AvatarWrapper>
              <AvatarLabel onClick={openFileInput}>
                <Upload size={14} style={{ marginRight: '4px' }} />
                Update Photo
              </AvatarLabel>
            </AvatarContainer>

            <FormSection>
              <FormField>
                <FieldLabel>
                  <User size={16} style={{ marginRight: '8px', display: 'inline' }} />
                  Your name
                </FieldLabel>
                <InputWrapper>
                  <StyledInput
                    type="text"
                    name="name"
                    value={ticketData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    required
                  />
                </InputWrapper>
              </FormField>

              <FormField>
                <FieldLabel>
                  Can you quickly describe your job, role or ambitions (CTO, UX designer, Student...)?
                </FieldLabel>
                <InputWrapper>
                  <StyledInput
                    type="text"
                    name="info"
                    value={ticketData.info}
                    onChange={handleInputChange}
                    placeholder="e.g., Software Engineer, Startup Founder, Student"
                    required
                  />
                </InputWrapper>
              </FormField>
            </FormSection>
          </AvatarSection>

          {/* Skills Slider Section */}
          {event?.tags && event.tags.length > 0 && (
            <SliderSection>
              <FieldLabel style={{ marginBottom: '16px' }}>
                Here you can add any field that you would like to learn about or share your knowledge with the community.
              </FieldLabel>
              {event.tags.map((tag, index) => (
                <CustomSliderComponent
                  key={index}
                  tag={tag}
                  value={getSliderValue(tag.name)}
                  onChange={handleSliderChange}
                />
              ))}
            </SliderSection>
          )}

          <NavigationSection>
            <PrimaryButton onClick={handleNext} disabled={isLoading}>
              <span>Next</span>
              <ChevronRight size={18} style={{ marginLeft: '8px' }} />
            </PrimaryButton>
          </NavigationSection>
        </div>
      )}

      {/* Step 2: Additional Info */}
      {activeStep === 2 && (
        <div>
          <FormField>
            <FieldLabel>
              <Link size={16} style={{ marginRight: '8px', display: 'inline' }} />
              Contact Information
            </FieldLabel>
            <div style={{ marginBottom: '8px', color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
              Are you happy to be contacted? Please share here your LinkedIn, Personal Website, Calendly, etc.
            </div>
            <InputWrapper>
              <StyledInput
                type="url"
                name="link_1"
                value={ticketData.link_1}
                onChange={handleInputChange}
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </InputWrapper>
          </FormField>

          <FormField>
            <FieldLabel>
              <Link size={16} style={{ marginRight: '8px', display: 'inline' }} />
              Portfolio or Project
            </FieldLabel>
            <div style={{ marginBottom: '8px', color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
              Any other website or project you would like to share? Please add your link here.
            </div>
            <InputWrapper>
              <StyledInput
                type="url"
                name="link_2"
                value={ticketData.link_2}
                onChange={handleInputChange}
                placeholder="https://yourwebsite.com or https://github.com/yourusername"
              />
            </InputWrapper>
          </FormField>

          <FormField>
            <FieldLabel>
              <MessageSquare size={16} style={{ marginRight: '8px', display: 'inline' }} />
              About You
            </FieldLabel>
            <div style={{ marginBottom: '8px', color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
              Any short description that you would like to share with the community?
            </div>
            <InputWrapper>
              <StyledTextarea
                name="description"
                value={ticketData.description}
                onChange={handleInputChange}
                placeholder="Tell us a bit about yourself, your interests, what you're working on..."
              />
            </InputWrapper>
          </FormField>

          <NavigationSection>
            <SecondaryButton onClick={handleBack} disabled={isLoading}>
              <ChevronLeft size={18} style={{ marginRight: '8px' }} />
              Previous
            </SecondaryButton>
            <PrimaryButton onClick={handleNext} disabled={isLoading}>
              Finish Setup
            </PrimaryButton>
          </NavigationSection>
        </div>
      )}
    </Container>
  )
}

EventTicketUpdate.propTypes = {
  visibleModal: PropTypes.func,
  myTicket: PropTypes.object,
  event: PropTypes.object,
  reload: PropTypes.func,
}

EventTicketUpdate.defaultProps = {
  visibleModal: () => null,
  myTicket: {},
  event: {},
  reload: () => null,
}

export default EventTicketUpdate